import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, ViewChild, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

export interface SelectOption {
  label: string;
  value: unknown;
}

type SelectSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './select.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  private static readonly dropdownGap = 6;
  private static readonly dropdownMinHeight = 180;

  private standaloneValue: unknown = '';
  private cvaDisabled = false;
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private _options: Array<string | SelectOption> = [];

  @Input() form?: FormGroup;
  @Input() controlName = '';
  @Input() control?: FormControl | AbstractControl | null;
  @Input() label = '';
  @Input() labelClass = 'mb-1 block text-sm font-medium text-slate900';
  @Input() placeholder = 'Select an option';
  @Input() set options(value: readonly (string | SelectOption)[] | null | undefined) {
    this._options = Array.isArray(value) ? [...value] : [];
  }
  get options(): Array<string | SelectOption> {
    return this._options;
  }
  @Input() error = '';
  @Input() emptyMessage = '';
  @Input() disabled = false;
  @Input() required = false;
  @Input() size: SelectSize = 'lg';
  @Input() upward = false;
  @Input() multiple = false;
  @Input() disableWhenEmpty = true;

  isOpen = false;
  menuStyle: Record<string, string> = {};

  @ViewChild('triggerButton') private triggerButton?: ElementRef<HTMLButtonElement>;

  get normalizedOptions(): SelectOption[] {
    return this.options.map((option) =>
      typeof option === 'string'
        ? { label: option, value: option }
        : option,
    );
  }

  get currentControl(): AbstractControl | null {
    if (this.control) {
      return this.control;
    }

    if (!this.form || !this.controlName) {
      return null;
    }

    return this.form.get(this.controlName);
  }

  get selectedValue(): unknown {
    return this.currentControl ? this.currentControl.value : this.standaloneValue;
  }

  get selectedValues(): unknown[] {
    if (!this.multiple) {
      return [];
    }

    const value = this.selectedValue;
    return Array.isArray(value) ? value : [];
  }

  get selectedLabel(): string {
    if (this.multiple) {
      const selected = this.normalizedOptions
        .filter((option) => this.selectedValues.includes(option.value))
        .map((option) => option.label);
      return selected.join(', ');
    }

    const selected = this.normalizedOptions.find((option) => option.value === this.selectedValue);
    return selected?.label ?? '';
  }

  get shouldShowError(): boolean {
    const control = this.currentControl;
    if (!control) {
      return !!this.error;
    }

    return !!this.error || (control.invalid && (control.dirty || control.touched));
  }

  get fallbackError(): string {
    const control = this.currentControl;
    if (!control?.errors) {
      return '';
    }

    if (control.errors['required']) {
      return `${this.label || 'This field'} is required.`;
    }

    return 'Invalid value.';
  }

  get hasOptions(): boolean {
    return this.normalizedOptions.length > 0;
  }

  get isEmptyDisabled(): boolean {
    return this.disableWhenEmpty && !this.hasOptions;
  }

  get resolvedEmptyMessage(): string {
    const customMessage = this.emptyMessage.trim();

    if (customMessage) {
      return customMessage;
    }

    const fieldName = this.label.trim() || 'options';
    return `No ${fieldName} available`;
  }

  toggle(): void {
    if (this.isDisabled) {
      return;
    }

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.updateMenuPosition();
    }
  }

  selectOption(option: SelectOption): void {
    if (this.isEmptyDisabled) {
      return;
    }

    const control = this.currentControl;
    if (!control) {
      this.updateValue(option.value);
      this.isOpen = false;
      return;
    }

    if (this.multiple) {
      const currentValues = this.selectedValues;
      const exists = currentValues.some((value) => value === option.value);
      const nextValues = exists
        ? currentValues.filter((value) => value !== option.value)
        : [...currentValues, option.value];
      control.setValue(nextValues);
      control.markAsDirty();
      control.markAsTouched();
      this.updateValue(nextValues);
      return;
    }

    control.setValue(option.value);
    control.markAsDirty();
    control.markAsTouched();
    this.updateValue(option.value);
    this.isOpen = false;
  }

  isOptionSelected(option: SelectOption): boolean {
    if (this.multiple) {
      return this.selectedValues.includes(option.value);
    }

    return this.selectedValue === option.value;
  }

  get triggerClassMap(): Record<string, boolean> {
    return {
      'h-[46px]': this.size === 'lg',
      'h-[40px]': this.size === 'md',
      'h-9': this.size === 'sm',
      'border-red500': this.shouldShowError,
      'border-slate300': !this.shouldShowError,
      'cursor-not-allowed bg-slate100 text-slate500': this.isDisabled || this.isEmptyDisabled,
      'bg-slate50': !this.isDisabled && !this.isEmptyDisabled,
    };
  }

  get triggerTextSizeClass(): string {
    return this.size === 'sm' ? 'text-xs' : 'text-sm';
  }

  get isDisabled(): boolean {
    return this.disabled || this.cvaDisabled || !!this.currentControl?.disabled;
  }

  writeValue(value: unknown): void {
    this.standaloneValue = value;
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isOpen) {
      return;
    }

    this.updateMenuPosition();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isOpen) {
      return;
    }

    this.updateMenuPosition();
  }

  @HostListener('document:scroll')
  onDocumentScroll(): void {
    if (!this.isOpen) {
      return;
    }

    this.updateMenuPosition();
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.app-select-root')) {
      this.isOpen = false;
    }
  }

  private updateMenuPosition(): void {
    if (!this.triggerButton) {
      return;
    }

    const rect = this.triggerButton.nativeElement.getBoundingClientRect();
    const boundary = this.getDropdownBoundaryRect();
    const gap = SelectComponent.dropdownGap;
    const spaceBelow = Math.max(boundary.bottom - rect.bottom - gap, 0);
    const spaceAbove = Math.max(rect.top - boundary.top - gap, 0);
    const shouldOpenAbove = this.upward;
    const maxHeight = Math.max(
      shouldOpenAbove ? spaceAbove : spaceBelow,
      SelectComponent.dropdownMinHeight,
    );

    this.menuStyle = {
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${Math.floor(maxHeight)}px`,
      top: shouldOpenAbove ? 'auto' : `${rect.bottom + gap}px`,
      bottom: shouldOpenAbove ? `${window.innerHeight - rect.top + gap}px` : 'auto',
    };
  }

  private updateValue(value: unknown): void {
    this.standaloneValue = value;
    this.onChange(value);
    this.onTouched();
  }

  private getDropdownBoundaryRect(): { top: number; bottom: number } {
    const trigger = this.triggerButton?.nativeElement;
    if (!trigger) {
      return { top: 0, bottom: window.innerHeight };
    }

    let top = 0;
    let bottom = window.innerHeight;
    let currentElement: HTMLElement | null = trigger.parentElement;

    while (currentElement && currentElement !== document.body) {
      const styles = window.getComputedStyle(currentElement);
      const overflowValues = [styles.overflow, styles.overflowY, styles.overflowX];
      const clipsContent = overflowValues.some((value) =>
        ['auto', 'scroll', 'hidden', 'clip'].includes(value),
      );

      if (clipsContent) {
        const rect = currentElement.getBoundingClientRect();
        top = Math.max(top, rect.top);
        bottom = Math.min(bottom, rect.bottom);
      }

      currentElement = currentElement.parentElement;
    }

    if (bottom <= top) {
      return { top: 0, bottom: window.innerHeight };
    }

    return { top, bottom };
  }
}
